import { datadogRum } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';

import {
  concatenatePathAndCatchAll,
  type RouteMatcher,
} from '@xing-com/crate-core-router';
import type { ErrorReporter } from '@xing-com/crate-xinglet';

export function setupDataDog(
  routeMatcherReference: { matchRoute?: RouteMatcher },
  {
    applicationId,
    clientToken,
    service = 'crate-app',
    sessionSampleRate,
    version,
    ...options
  }: Partial<RumInitConfiguration> = {}
): ErrorReporter {
  const env = {
    'www.xing.com': 'production' as const,
    'preview.xing.com': 'preview' as const,
  }[document.location.hostname];

  if (!env || !applicationId || !clientToken) {
    return function ignoreError() {
      //
    };
  }

  datadogRum.init({
    allowedTracingUrls: [
      /^https:\/\/.*?\.xing\.com/,
      /^https:\/\/.*?\.lebenslauf\.com/,
      /^https:\/\/.*?\.anschreiben\.com/,
      /^https:\/\/.*?\.lebenslauf\.cc/,
      /^https:\/\/.*?\.anschreiben\.cc/,
      /^https:\/\/.*?\.nwse.io/,
    ],
    applicationId,
    clientToken,
    defaultPrivacyLevel: 'mask',
    env,
    service,
    sessionReplaySampleRate: 0,
    sessionSampleRate,
    traceSampleRate: 1,
    traceContextInjection: 'sampled',
    site: 'datadoghq.eu',
    version,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    ...options,
    beforeSend: (event, _context) => {
      const { matchRoute } = routeMatcherReference;
      const match = matchRoute?.(window.location.pathname);
      if (!match) return true;
      const { path, owner, catchAll } = match;

      event.view.name = concatenatePathAndCatchAll(path, catchAll);
      if (owner) Object.assign((event.context ??= {}), { owner });

      return true;
    },
  });

  datadogRum.setGlobalContextProperty('cost_center', '11131');
  datadogRum.setGlobalContextProperty('application', 'crate');

  return function reportError(error, context) {
    datadogRum.addError(error, context);
  };
}
